/**
 * user-prefs.js
 * Modular User Preferences for SweldoNow
 * Future updates - auto save to DB then auto sync to all devices
 */

import rowPerPage from '@/constants/rows-per-page.js';

const DEFAULT_ROWS = 5;
const ALLOWED_ROWS = rowPerPage.map(item => (typeof item === 'number' ? item : 'All'));

// --- ROWS PER PAGE ---
export function getRowsPerPage(pageName) {
    if (!pageName) return DEFAULT_ROWS;
    const stored = localStorage.getItem(`rowsPerPage_${pageName}`);
    if (stored === null) return DEFAULT_ROWS;
    const value = stored === 'All' ? -1 : parseInt(stored);
    return ALLOWED_ROWS.includes(stored === 'All' ? 'All' : value) ? value : DEFAULT_ROWS;
}

export function updateRowsPerPage(pageName, rows) {
    if (!pageName) return;
    const storeValue = rows === -1 ? 'All' : rows;
    if (!ALLOWED_ROWS.includes(storeValue)) return;
    localStorage.setItem(`rowsPerPage_${pageName}`, storeValue);
}

// --- VIEW ALL SWITCH ---
export function getViewAllSwitch(pageName) {
    if (!pageName) return false;
    const stored = localStorage.getItem(`viewAllSwitch_${pageName}`);
    return stored === 'true';
}

export function updateViewAllSwitch(pageName, value) {
    if (!pageName) return;
    localStorage.setItem(`viewAllSwitch_${pageName}`, value ? 'true' : 'false');
}

// --- SORTING ---
export function getSort(pageName) {
    if (!pageName) return { sortBy: null, descending: false };
    const stored = localStorage.getItem(`sort_${pageName}`);
    return stored ? JSON.parse(stored) : { sortBy: null, descending: false };
}

export function updateSort(pageName, sortBy, descending) {
    if (!pageName) return;
    localStorage.setItem(`sort_${pageName}`, JSON.stringify({ sortBy, descending }));
}

// --- CALENDAR & COMPENSATION ---
export function getCalendarYear(pageName) {
    if (!pageName) return null;
    const stored = localStorage.getItem(`calendarYear_${pageName}`);
    return stored ? parseInt(stored) : new Date().getFullYear();
}

export function updateCalendarYear(pageName, calendarYear) {
    if (!pageName || isNaN(calendarYear)) return;
    localStorage.setItem(`calendarYear_${pageName}`, calendarYear.toString());
}

export function getSelectedCompensationYear() {
    const stored = localStorage.getItem('selectedCompensationYear');
    return stored || new Date().getFullYear();
}

export function updateSelectedCompensationYear(year) {
    if (year) localStorage.setItem('selectedCompensationYear', year);
}

// --- SEARCH ---
export function getSearchKeyword(pageName) {
    if (!pageName) return null;
    const stored = localStorage.getItem(`searchKeyword_${pageName}`);
    return stored && stored.trim() !== '' ? stored : null;
}

export function updateSearchKeyword(pageName, value) {
    if (!pageName) return;
    if (!value || value.trim() === '') {
        localStorage.removeItem(`searchKeyword_${pageName}`);
    } else {
        localStorage.setItem(`searchKeyword_${pageName}`, value);
    }
}

// --- VISIBILITY (EXPAND/COLLAPSE) ---
export function getExpandCollapseVisibility(pageName, section) {
    if (!pageName) return false;
    const stored = localStorage.getItem(`Expand_Collapse_Visibility_${pageName}_${section}`);
    return stored === null ? true : stored === 'true';
}

export function updateExpandCollapseVisibility(pageName, section, value) {
    if (!pageName) return;
    localStorage.setItem(`Expand_Collapse_Visibility_${pageName}_${section}`, value ? 'true' : 'false');
}

// --- DEVICE LOGIN (WEBAUTHN) COOLDOWNS ---

/**
 * Checks if the prompt should be hidden based on "Not Now" (1 day) or "Remind Later" (7 days).
 */

export function getHideDeviceLoginPrompt() {
    const now = Date.now();

    // 1 Day Cooldown
    const notNowTs = localStorage.getItem('deviceLogin_notNowCooldown');
    if (notNowTs && (now - parseInt(notNowTs)) < (24 * 60 * 60 * 1000)) return true;

    return false;
}

export function setDeviceLoginNotNow() {
    localStorage.setItem('deviceLogin_notNowCooldown', Date.now().toString());
}

export function clearDeviceLoginCooldowns() {
    localStorage.removeItem('deviceLogin_notNowCooldown');
    localStorage.removeItem('deviceLogin_remindMeLater');
}
